<script setup lang="ts">
import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  ref,
  useAttrs,
  watch,
  type ComputedRef,
} from 'vue'

import {
  getColorStyleData as cssColor,
  composeShadow,
  getSizeStyleData as cssSize,
  getSpacingStyleData as cssSpacing,
} from '@render/composables/useStyleData'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import { ERROR_CONTEXTS, PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { useDirection } from '@render/composables/useDirection'
import TipTap from '@render/lib/TipTap'
import PlainImage from '../../generic/PlainImage.vue'
import ButtonLoader from '../../generic/ButtonLoader.vue'

// Type-only import — erased at compile time, so the editor-only definition
// never enters a built-pipe bundle (see reference/06-component-metadata.md).
// The field value shapes come from `definition.fields` via `FieldValues<>`, so
// this component and its schema can't drift apart (task 013).
import type { CTAButtonFields as Props } from './definition'

// Disable Vue's automatic attribute fallthrough. With it on, RenderComponents'
// `v-on=getEventListeners(...)` auto-binds the parent's `onClick` listener to
// the `<button>` root as a native click handler — that bypasses our wrapper,
// so the disable-while-pending state never engages. We read `attrs.onClick`
// manually in `fireClick`, and pass non-listener attrs (data-id etc.)
// through via `passthroughAttrs`.
defineOptions({ inheritAttrs: false })

const pipe = usePipe()
const attrs = useAttrs()
const { isRtl } = useDirection()
const fields = defineModel<Props>('fields', {
  default: () => ({ interactionStateSelect: 'idle' }),
})

// Form-group wiring — when not opted-out via `doNotSubmitForm`, the CTA
// registers itself as the nearest form group's submit target so soft-keyboard
// Enter on an input can fire the CTA's full click flow.
const ancestry = inject<ComputedRef<readonly string[]>>(
  'sharedNodeRefAncestry',
  computed(() => []),
)
const formGroupKey = inject<ComputedRef<string | null> | null>('formGroupKey', null)
const instanceKey = computed<string | null>(() => {
  const nodeId = attrs['data-id'] as string | undefined
  if (!nodeId) return null
  return [...ancestry.value, nodeId].join('/')
})

const pendingCount = ref(0)
const isPending = computed(() => pendingCount.value > 0)

const isDisabled = computed(() => {
  const key = fields.value.isEnabledVariable?.key
  const externallyDisabled = key ? !pipe.getData(`var.${key}`) : false
  const awaitHandlers = !fields.value.doNotAwaitHandlers
  return externallyDisabled || (awaitHandlers && isPending.value)
})

// Guard from leaking preview class to prod
const previewClass = computed(() => {
  if (pipe.currentMode.value !== PipeDisplayMode.EDITOR) return null
  return `is-${fields.value.interactionStateSelect || 'idle'}`
})

// Editor's drag-to-link flow needs `dragstart` to fire on the CTA, which
// requires `-webkit-user-drag: element`. That CSS rule has a known iOS WebKit
// side-effect — taps with micro-movement get interpreted as drag-starts and
// their click is suppressed. We scope the rule to modes where the editor is
// active (EDITOR + INTERACTIVE_VIEW) and keep it off in VIEW / STATIC.
const isEditorDraggable = computed(() => {
  const mode = pipe.currentMode.value
  return mode === PipeDisplayMode.EDITOR || mode === PipeDisplayMode.INTERACTIVE_VIEW
})

const value = computed({
  get: () => pipe.getTranslation(fields.value.label?.key),
  set: (newVal: object) => {
    // Every FieldValues<> key is optional (a stored pipe may omit any field),
    // so guard rather than relying on the definition declaring `label`.
    const key = fields.value.label?.key
    if (key) pipe.setTranslation(key, newVal)
  },
})

const styles = computed(() => {
  const small = fields.value.isSmall
  return {
    bgColor: cssColor(fields.value.bgColor, 'var(--color-action-primary-background)'),
    bgColorHover: cssColor(
      fields.value.bgColorHover,
      'var(--color-action-primary-background-hover)',
    ),
    bgColorActive: cssColor(
      fields.value.bgColorActive,
      'var(--color-action-primary-background-pressed)',
    ),
    bgColorDisabled: cssColor(
      fields.value.bgColorDisabled,
      'var(--color-action-primary-background-disabled)',
    ),
    textColor: cssColor(fields.value.textColor, 'var(--color-action-primary-text)'),
    textColorDisabled: cssColor(
      fields.value.textColorDisabled,
      'var(--color-action-primary-text-disabled)',
    ),
    fontSize: cssSize(fields.value.textSize, 'var(--text-button-size)'),
    lineHeight: cssSize(fields.value.textLineHeight, 'var(--text-button-line-height)'),
    fontWeight: fields.value.textFontWeight ?? 'var(--text-button-weight)',
    textAlign: fields.value.textAlign ?? 'center',
    borderColor: cssColor(fields.value.borderColor, 'var(--color-action-primary-border)'),
    focusBorderColor: cssColor(
      fields.value.focusBorderColor,
      'var(--color-action-primary-border-pressed)',
    ),
    borderColorDisabled: cssColor(
      fields.value.borderColorDisabled,
      'var(--color-action-primary-border-disabled)',
    ),
    borderRadius: cssSize(fields.value.borderRadius, 'var(--radius-xs)'),
    borderWidth: cssSize(fields.value.borderWidth, 'var(--border-width)'),
    paddingY: cssSize(fields.value.paddingY, 'var(--space-8)'),
    paddingX: cssSize(fields.value.paddingX, small ? 'var(--space-12)' : 'var(--space-24)'),
    gap: cssSize(fields.value.gapColumns, small ? 'var(--space-8)' : 'var(--space-12)'),
    margin: cssSpacing(fields.value.margin, '0 auto'),
    minHeight: cssSize(fields.value.minHeight, small ? '40px' : '78px'),
    maxWidth: cssSize(fields.value.maxWidth, small ? '432px' : '480px'),
    timeoutBgColor: cssColor(
      fields.value.timeoutBgColor,
      'var(--color-action-primary-background-pressed)',
    ),
    timeoutBgOpacity: String(fields.value.timeoutBgOpacity ?? 0.3),
    imageHeight: cssSize(fields.value.imageHeight, '40px'),
    loaderColor:
      cssColor(fields.value.loaderColor, 'var(--color-secondary-100)') ??
      'var(--color-secondary-100)',
    loaderTrackColor:
      cssColor(fields.value.loaderTrackColor, 'var(--color-primary-500)') ??
      'var(--color-primary-500)',
  }
})

// Editor-only flag toggled by the "Preview: Loader" command button so authors
// can see the spinner without triggering a real pending click.
const isPreviewingLoader = ref(false)

// While the click handlers (or the form-submit validation gate) are pending,
// swap the label/images for a spinner. Opt-in via `showLoader: true`.
const showLoader = computed(
  () => fields.value.showLoader === true && (isPending.value || isPreviewingLoader.value),
)

// Drop + inner shadow are admin-controlled across every interactive state via
// the Drop Shadow / Inner Shadow fields: unset keeps that state's DS default,
// "None" removes the side, or pick a token. Disabled stays shadow-less below.
const idleShadow = computed(() =>
  composeShadow(
    fields.value.dropShadow,
    fields.value.innerShadow,
    'var(--shadow-cta-button-default)',
    'var(--shadow-inner-glow-cta-button-default)',
  ),
)
const hoverShadow = computed(() =>
  composeShadow(
    fields.value.dropShadow,
    fields.value.innerShadow,
    'var(--shadow-cta-button-hover)',
    'var(--shadow-inner-glow-cta-button-hover)',
  ),
)
const activeShadow = computed(() =>
  composeShadow(
    fields.value.dropShadow,
    fields.value.innerShadow,
    'var(--shadow-cta-button-pressed)',
    'var(--shadow-inner-glow-cta-button-pressed)',
  ),
)

// Images support
const showImage = computed(() => fields.value.showImage === true)
const imagePosition = computed(() => fields.value.imagePosition ?? 'left')
const primaryImage = computed(() => fields.value.imagePrimary)
const secondaryImage = computed(() => fields.value.imageSecondary)
const imageSkeletonBg = computed(() => cssColor(fields.value.imageSkeletonBgColor) ?? 'transparent')
const imageSkeletonShimmer = computed(
  () => cssColor(fields.value.imageSkeletonShimmerColor) ?? 'transparent',
)
// In RTL the image is flipped by default - the flag turns that off. The side
// (left/right) swaps on its own in an RTL row.
const imageMirror = computed(() =>
  isRtl.value && fields.value.doNotMirrorImageOnRtl !== true ? 'scaleX(-1)' : 'none',
)
// Reserve width from the image's aspect ratio so the label does not jump when
// the image loads. Uses a square when the size is unknown.
const imgMinWidth = (img?: { width?: number; height?: number }) => {
  const aspect = img?.width && img?.height ? img.width / img.height : 1
  return `min(calc(${styles.value.imageHeight} * ${aspect}), 112px)`
}
const imageStyle = computed(() => ({
  order: imagePosition.value === 'right' ? 2 : 0,
  minWidth: imgMinWidth(primaryImage.value),
}))
const imageStyleBoth = (side: 'left' | 'right') => ({
  order: side === 'left' ? 0 : 100,
  minWidth: imgMinWidth(side === 'right' ? secondaryImage.value : primaryImage.value),
})

const animationDurationCss = computed(() => {
  const sec = Math.max(0, Number(fields.value.timeoutSeconds) || 0)
  return `${sec}s`
})

const isAnimating = ref(false)
let timeoutId: ReturnType<typeof setTimeout> | null = null

function startTimeout(opts: { fireOnComplete: boolean }) {
  const sec = Math.max(0, Number(fields.value.timeoutSeconds) || 0)
  if (sec <= 0) return
  cancelTimeout()
  isAnimating.value = true
  timeoutId = setTimeout(() => {
    timeoutId = null
    isAnimating.value = false
    if (opts.fireOnComplete && !isDisabled.value) {
      fireClick(null)
    }
  }, sec * 1000)
}

// Forward all parent-bound attrs EXCEPT listeners — we invoke the parent's
// `onClick` ourselves through `fireClick`. Listener keys in Vue's $attrs are
// `on` followed by an uppercase letter (e.g. `onClick`, `onMouseEnter`).
const passthroughAttrs = computed(() => {
  const result: Record<string, unknown> = {}
  for (const key in attrs) {
    if (/^on[A-Z]/.test(key)) continue
    result[key] = attrs[key]
  }
  return result
})

async function fireClick(event: MouseEvent | null) {
  // Listener key is `onOnClick`, not `onClick`. Reason: the parent
  // (`RenderComponents.vue`) binds `v-on=getEventListeners(...)` whose returned
  // record is keyed by the schema event name `onClick`. Vue's v-on object
  // syntax then prepends `on` + capitalize(name) when normalizing into vnode
  // props — so the schema name `onClick` becomes the runtime key `onOnClick`.
  // Fall back to plain `onClick` for safety in case Vue's normalization ever
  // changes or another caller binds the listener directly.
  const handler = (attrs.onOnClick ?? attrs.onClick) as
    | ((e: unknown) => unknown)
    | ((e: unknown) => unknown)[]
    | undefined

  // Form-submit gating — validates every input in the nearest form group
  // before invoking onClick handlers. `pendingCount++` runs through the
  // validation step too, so the button shows disabled while async validators
  // resolve (e.g. server-side email-uniqueness check). `submitFormGroup` is
  // a no-op pass-through in EDITOR mode, so this doesn't interfere with
  // authoring. Validation is a precondition — `doNotAwaitHandlers` only
  // governs the handler-await step, not this gate.
  const groupKey = formGroupKey?.value
  if (!fields.value.doNotSubmitForm && groupKey) {
    pendingCount.value++
    let ok = false
    try {
      ok = await pipe.submitFormGroup(groupKey)
    } finally {
      pendingCount.value--
    }
    if (!ok) return
  }

  if (!handler) return
  const fns = Array.isArray(handler) ? handler : [handler]
  if (fields.value.doNotAwaitHandlers) {
    // Opt-out: fire and don't await — preserves rapid-click semantics.
    fns.forEach((fn) => fn(event))
    return
  }
  pendingCount.value++
  try {
    await Promise.all(fns.map((fn) => fn(event)))
  } catch (e) {
    pipe.trackError({
      error: e,
      context: ERROR_CONTEXTS.ctaButtonClickHandler,
      metadata: { nodeId: attrs['data-id'] },
    })
  } finally {
    pendingCount.value--
  }
}

function cancelTimeout() {
  if (timeoutId !== null) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
  isAnimating.value = false
}

useEditorCommands({
  previewTimeout: () => startTimeout({ fireOnComplete: false }),
  resetTimeout: () => cancelTimeout(),
  toggleLoader: () => {
    isPreviewingLoader.value = !isPreviewingLoader.value
  },
})

// Submit-target registration — registers on mount, re-runs when instance
// key changes (SNR reuse during nav) or when the user toggles
// `doNotSubmitForm` in the editor.
let lastSubmitGroup: string | null = null
let lastSubmitKey: string | null = null
const registerAsSubmitTarget = (group: string, key: string) => {
  pipe.registerFormSubmitTarget(group, {
    sourceKey: key,
    submit: async () => {
      await fireClick(null)
    },
  })
  lastSubmitGroup = group
  lastSubmitKey = key
}
const unregisterAsSubmitTarget = () => {
  if (lastSubmitGroup && lastSubmitKey) {
    pipe.unregisterFormSubmitTarget(lastSubmitGroup, lastSubmitKey)
  }
  lastSubmitGroup = null
  lastSubmitKey = null
}
const syncSubmitTarget = () => {
  unregisterAsSubmitTarget()
  const key = instanceKey.value
  const group = formGroupKey?.value
  if (!fields.value.doNotSubmitForm && key && group) registerAsSubmitTarget(group, key)
}

onMounted(() => {
  syncSubmitTarget()
  if (pipe.currentMode.value !== PipeDisplayMode.EDITOR && (fields.value.timeoutSeconds ?? 0) > 0) {
    startTimeout({ fireOnComplete: true })
  }
})

watch(
  [instanceKey, () => formGroupKey?.value, () => fields.value.doNotSubmitForm],
  syncSubmitTarget,
)

onBeforeUnmount(() => {
  cancelTimeout()
  unregisterAsSubmitTarget()
})

// One `@click` isn't enough here. On touch, tapping the button closes the
// keyboard and shifts the page mid-tap, so the browser's `click` lands on the
// wrong (shifted) element. So activation is rebuilt from lower-level events, as
// three jobs across two input models:
//   ACTIVATE - pointerdown + pointerup (finger/mouse), click (keyboard). pointerup
//     survives the page shift (pointer capture), but capture also delivers a
//     scroll that merely started on the button, so we fire only if the finger
//     barely moved since pointerdown (DRAG_THRESHOLD). click is gated to keyboard
//     (Enter/Space, detail 0) so a pointer tap can't also double-fire here.
//   SUPPRESS - touchstart preventDefault kills the browser's delayed "ghost" click
//     that would otherwise hit the next page after we navigate. (Trade-off: it
//     also disables scrolling that starts on the button.)
//   REPAINT - touchend/touchcancel restore the native :active press style that the
//     touchstart preventDefault suppresses on Android, via `isPressed`.
// Editor-draggable modes activate on click (their pointerup is the drag-to-link release).

const isPressed = ref(false)
const clearPressed = () => {
  isPressed.value = false
}

// Finger/mouse position at pointerdown, used to distinguish a tap from a drag on pointerup
const DRAG_THRESHOLD = 10
let pointerDownX = 0
let pointerDownY = 0

function handlePointerdown(event: PointerEvent) {
  pointerDownX = event.clientX
  pointerDownY = event.clientY
}

function handleTouchstart(event: TouchEvent) {
  if (isEditorDraggable.value) return
  event.preventDefault() // SUPPRESS: kill the browser's ghost click
  if (!isDisabled.value) isPressed.value = true // REPAINT: drive :active ourselves
}

function handlePointerup(event: PointerEvent) {
  if (isEditorDraggable.value) return // editor: pointerup is the drag-to-link release
  if (isDisabled.value) return
  // Ignore a scroll/drag that merely started on the button
  if (Math.hypot(event.clientX - pointerDownX, event.clientY - pointerDownY) > DRAG_THRESHOLD)
    return
  cancelTimeout()
  fireClick(event) // ACTIVATE: finger/mouse
}

function handleClick(event: MouseEvent) {
  if (!isEditorDraggable.value && event.detail !== 0) return // ACTIVATE: keyboard only
  if (isDisabled.value) return
  cancelTimeout()
  fireClick(event)
}
</script>

<template>
  <button
    type="button"
    :class="[
      'cta-button',
      previewClass,
      {
        'is-small': fields.isSmall,
        'is-disabled': isDisabled,
        'is-editor-draggable': isEditorDraggable,
        'is-pressed': isPressed,
      },
    ]"
    :aria-disabled="isDisabled || undefined"
    v-bind="passthroughAttrs"
    @pointerdown="handlePointerdown"
    @touchstart="handleTouchstart"
    @touchend="clearPressed"
    @touchcancel="clearPressed"
    @pointerup="handlePointerup"
    @click="handleClick"
  >
    <div v-if="isAnimating" class="cta-button__timeout-overlay" />

    <!-- Pending spinner — replaces the label/images while handlers run. -->
    <ButtonLoader
      v-if="showLoader"
      class="cta-button__loader"
      :color="styles.loaderColor"
      :track-color="styles.loaderTrackColor"
    />

    <!-- Single image (left or right) -->
    <div
      v-if="!showLoader && showImage && imagePosition !== 'both' && primaryImage?.id"
      class="cta-button__image"
      :style="imageStyle"
    >
      <PlainImage
        :image="primaryImage"
        :skeleton-color="imageSkeletonBg"
        :shimmer-color="imageSkeletonShimmer"
        class="cta-button__img"
      />
    </div>

    <!-- Both: primary on the left -->
    <div
      v-if="!showLoader && showImage && imagePosition === 'both' && primaryImage?.id"
      class="cta-button__image"
      :style="imageStyleBoth('left')"
    >
      <PlainImage
        :image="primaryImage"
        :skeleton-color="imageSkeletonBg"
        :shimmer-color="imageSkeletonShimmer"
        class="cta-button__img"
      />
    </div>

    <div v-if="!showLoader" class="cta-button__label">
      <div v-if="!fields.label">No translation has been set!</div>
      <TipTap v-else v-model="value" mode="single" />
    </div>

    <!-- Both: secondary on the right -->
    <div
      v-if="!showLoader && showImage && imagePosition === 'both' && secondaryImage?.id"
      class="cta-button__image"
      :style="imageStyleBoth('right')"
    >
      <PlainImage
        :image="secondaryImage"
        :skeleton-color="imageSkeletonBg"
        :shimmer-color="imageSkeletonShimmer"
        class="cta-button__img"
      />
    </div>
  </button>
</template>

<style scoped lang="scss">
.cta-button {
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  overflow: hidden;
  font-family: var(--text-button-family);
  font-weight: v-bind('styles.fontWeight');
  font-size: v-bind('styles.fontSize');
  line-height: v-bind('styles.lineHeight');
  text-align: v-bind('styles.textAlign');

  min-height: v-bind('styles.minHeight');
  max-width: v-bind('styles.maxWidth');
  margin: v-bind('styles.margin');
  padding: v-bind('styles.paddingY') v-bind('styles.paddingX');
  gap: v-bind('styles.gap');

  border-width: v-bind('styles.borderWidth');
  border-style: solid;
  border-radius: v-bind('styles.borderRadius');
  border-color: v-bind('styles.borderColor');

  background-color: v-bind('styles.bgColor');
  color: v-bind('styles.textColor');

  box-shadow: v-bind('idleShadow');

  &:hover:not(:disabled, .is-disabled),
  &.is-hover,
  &:focus-visible:not(:disabled, .is-disabled) {
    background-color: v-bind('styles.bgColorHover');
    box-shadow: v-bind('hoverShadow');
  }

  &:active:not(:disabled, .is-disabled),
  &.is-pressed {
    background-color: v-bind('styles.bgColorActive');
    border-color: v-bind('styles.focusBorderColor');
    box-shadow: v-bind('activeShadow');
  }

  /* Editor drag-to-link flow only — applies in EDITOR + INTERACTIVE_VIEW
     modes via the `is-editor-draggable` class. `all: unset` above resets
     `-webkit-user-drag` (set by [data-id] in PreviewApp.vue) to `auto`,
     which suppresses HTML5 drag — restore it scoped here. Kept OFF in
     VIEW / STATIC because on iOS WebKit it makes taps with micro-movement
     get interpreted as drag-starts, suppressing the resulting click. */
  &.is-editor-draggable {
    -webkit-user-drag: element;
  }

  &:disabled,
  &.is-disabled {
    background-color: v-bind('styles.bgColorDisabled');
    color: v-bind('styles.textColorDisabled');
    border-color: v-bind('styles.borderColorDisabled');
    cursor: not-allowed;
    box-shadow: none;
  }

  transition:
    color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    background-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1);

  > :not(.cta-button__timeout-overlay) {
    position: relative;
    z-index: 1;
  }

  .cta-button__label {
    flex: 1 1 auto;
    min-width: 0;
    order: 1;
  }

  .cta-button__loader {
    flex: 1 1 auto;
    justify-content: center;
  }

  .cta-button__image {
    flex-shrink: 0;
    height: v-bind('styles.imageHeight');
    max-width: 112px;

    .cta-button__img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
      // In RTL the image is flipped by default (turn off with doNotMirrorImageOnRtl).
      transform: v-bind('imageMirror');
    }
  }
}

.cta-button__timeout-overlay {
  position: absolute;
  inset: 0 auto 0 0;
  width: 0;
  background-color: v-bind('styles.timeoutBgColor');
  opacity: v-bind('styles.timeoutBgOpacity');
  pointer-events: none;
  z-index: 0;
  animation: cta-timeout-slide v-bind('animationDurationCss') linear forwards;
}

@keyframes cta-timeout-slide {
  from {
    width: 0%;
  }
  to {
    width: 100%;
  }
}
</style>
